import { useState, useRef, useEffect, type FormEvent } from "react";
import { useAuth } from "../hooks/useAuth";
import { api } from "../lib/api";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import { Lock } from "lucide-react";
import styles from "./LoginPage.module.css";

interface LoginPageProps {
  initialInviteToken?: string;
}

export function LoginPage({ initialInviteToken }: LoginPageProps) {
  const { login, register } = useAuth();
  const [mode, setMode] = useState<"login" | "register" | "forgot" | "reset">(
    initialInviteToken ? "register" : "login"
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [showTotp, setShowTotp] = useState(false);
  const [trustDevice, setTrustDevice] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [inviteToken, setInviteToken] = useState(initialInviteToken ?? "");

  // Password reset fields
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");

  // Idle-logout notice
  const [idleNotice, setIdleNotice] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("reason") === "idle") {
      // Clean up URL
      params.delete("reason");
      const clean = params.toString();
      window.history.replaceState({}, "", clean ? `?${clean}` : window.location.pathname);
      return true;
    }
    return false;
  });

  // Also listen for idle-logout event (in case component is already mounted)
  useEffect(() => {
    const handler = () => setIdleNotice(true);
    window.addEventListener("idle-logout", handler);
    return () => window.removeEventListener("idle-logout", handler);
  }, []);

  // Anti-bot: timestamp when form loaded
  const formLoadTime = useRef(Date.now());
  // Anti-bot: honeypot
  const [honeypot, setHoneypot] = useState("");

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      if (mode === "login") {
        await login(email, password, showTotp ? totpCode : undefined, trustDevice);
      } else if (mode === "register") {
        await register(email, displayName, password, {
          website: honeypot,
          _t: formLoadTime.current,
          ...(inviteToken ? { inviteToken } : {}),
        });
      } else if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email }, { auth: false });
        setSuccess("Falls die E-Mail existiert, wurde ein Reset-Code versendet.");
        setMode("reset");
      } else if (mode === "reset") {
        await api.post("/auth/reset-password", { email, token: resetToken, newPassword }, { auth: false });
        setSuccess("Passwort erfolgreich geändert! Du kannst dich jetzt anmelden.");
        setResetToken("");
        setNewPassword("");
        setMode("login");
      }
    } catch (err) {
      const message = (err as { message?: string }).message ?? "Fehler aufgetreten";
      if (message.includes("totp") || message.includes("2FA")) {
        setShowTotp(true);
        setError("Bitte 2FA-Code eingeben");
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        {/* Logo / Brand */}
        <div className={styles.brand}>
          <div className={styles.logo}>
            <img src="/logo.png" alt="KollegePlus" className={styles.logoImg} />
          </div>
          <h1 className={styles.title}>KollegePlus</h1>
          <p className={styles.subtitle}>Sichere Kommunikation für Teams</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className={styles.form}>
          {mode === "register" && (
            <Input
              label="Name"
              type="text"
              placeholder="Dein Anzeigename"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
              autoComplete="name"
            />
          )}

          {/* Honeypot — invisible to humans, bots auto-fill it */}
          {mode === "register" && (
            <div style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, overflow: "hidden" }} aria-hidden="true">
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
              />
            </div>
          )}

          {(mode === "login" || mode === "register" || mode === "forgot" || mode === "reset") && (
            <Input
              label="E-Mail"
              type="email"
              placeholder="name@firma.de"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          )}

          {(mode === "login" || mode === "register") && (
            <Input
              label="Passwort"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete={mode === "login" ? "current-password" : "new-password"}
            />
          )}

          {mode === "register" && (
            <Input
              label="Einladungscode (optional)"
              type="text"
              placeholder="Einladungscode eingeben"
              value={inviteToken}
              onChange={(e) => setInviteToken(e.target.value)}
              autoComplete="off"
            />
          )}

          {mode === "reset" && (
            <>
              <Input
                label="Reset-Code"
                type="text"
                placeholder="6-stelliger Code"
                value={resetToken}
                onChange={(e) => setResetToken(e.target.value)}
                required
                maxLength={6}
                autoComplete="one-time-code"
              />
              <Input
                label="Neues Passwort"
                type="password"
                placeholder="Neues Passwort (min. 12 Zeichen)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                autoComplete="new-password"
              />
            </>
          )}

          {showTotp && mode === "login" && (
            <Input
              label="2FA-Code"
              type="text"
              placeholder="123456"
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value)}
              autoComplete="one-time-code"
              maxLength={6}
            />
          )}

          {showTotp && mode === "login" && (
            <label className={styles.trustDevice}>
              <input
                type="checkbox"
                checked={trustDevice}
                onChange={(e) => setTrustDevice(e.target.checked)}
              />
              <span>Diesem Gerät 30 Tage vertrauen</span>
            </label>
          )}

          {idleNotice && (
            <div className={styles.success}>
              Du wurdest wegen Inaktivität automatisch abgemeldet.
            </div>
          )}
          {error && <div className={styles.error}>{error}</div>}
          {success && <div className={styles.success}>{success}</div>}

          <Button type="submit" loading={loading} size="lg" className={styles.submitButton}>
            {mode === "login" && "Anmelden"}
            {mode === "register" && "Konto erstellen"}
            {mode === "forgot" && "Reset anfordern"}
            {mode === "reset" && "Passwort ändern"}
          </Button>
        </form>

        {/* Forgot password link */}
        {mode === "login" && (
          <div className={styles.forgotPassword}>
            <button
              type="button"
              className={styles.link}
              onClick={() => {
                setMode("forgot");
                setError("");
                setSuccess("");
              }}
            >
              Passwort vergessen?
            </button>
          </div>
        )}

        {/* Toggle */}
        <div className={styles.toggle}>
          {mode === "login" ? (
            <p>
              Noch kein Konto?{" "}
              <button
                type="button"
                className={styles.link}
                onClick={() => {
                  setMode("register");
                  setError("");
                  setSuccess("");
                }}
              >
                Registrieren
              </button>
            </p>
          ) : (
            <p>
              {mode === "forgot" || mode === "reset" ? "Code erhalten? " : "Bereits registriert? "}
              <button
                type="button"
                className={styles.link}
                onClick={() => {
                  if (mode === "forgot") {
                    setMode("reset");
                  } else {
                    setMode("login");
                  }
                  setError("");
                  setSuccess("");
                }}
              >
                {mode === "forgot" ? "Code eingeben" : "Anmelden"}
              </button>
            </p>
          )}
        </div>

        {/* Security Badge */}
        <div className={styles.security}>
          <Lock size={14} /> Ende-zu-Ende verschlüsselt
        </div>
      </div>
    </div>
  );
}
